import { Injectable } from '@angular/core';
import { injectPaymentsTracking } from './payments-tracking.providers';
import { buildActiveFiltersString } from './payment-history-tracking.utils';
import {
  PaymentHistoryBaseMetadata,
  PaymentHistoryStepMetadataInput,
  PaymentHistorySurface,
  PaymentsHistoryStep,
} from './payment-history-workflow.models';
import { PaymentsWorkflowStepMetaDataMap } from './payments-workflow.models';

interface PaymentHistoryTrackingContext {
  aiwynPaymentsOnly: boolean;
  dateRangeDays: number | null;
  surface: PaymentHistorySurface;
  searchTerm: string;
  columnFilterIds: string[];
}

/**
 * Per-container tracking facade for Payment History. Wraps `TrackWorkflowService` and stamps
 * `PaymentHistoryBaseMetadata` onto every event so call sites only pass step extras.
 *
 * The shared payment-history table is mounted by two surfaces (`STANDALONE` Payments page,
 * `CLIENT_DETAIL` payments tab). Each surface owns its own context, so the service is
 * registered per-container (`providers: [PaymentHistoryTrackService]`) — root scope would
 * let one surface's `context` leak into the other on navigation.
 */
@Injectable()
export class PaymentHistoryTrackService {
  private readonly tracker = injectPaymentsTracking();

  private context: PaymentHistoryTrackingContext = {
    aiwynPaymentsOnly: true,
    dateRangeDays: null,
    surface: 'STANDALONE',
    searchTerm: '',
    columnFilterIds: [],
  };

  setContext(context: Partial<PaymentHistoryTrackingContext>): void {
    this.context = { ...this.context, ...context };
  }

  trackWorkflow<E extends PaymentsHistoryStep>(step: E, metadata?: PaymentHistoryStepMetadataInput<E>): void {
    const merged = { ...this.resolveBaseMetadata(), ...(metadata ?? {}) } as PaymentsWorkflowStepMetaDataMap[E];
    this.tracker.trackWorkflow(step, merged);
  }

  private resolveBaseMetadata(): PaymentHistoryBaseMetadata {
    return {
      aiwynPaymentsOnly: this.context.aiwynPaymentsOnly,
      dateRangeDays: this.context.dateRangeDays,
      surface: this.context.surface,
      activeFilters: buildActiveFiltersString({
        aiwynPaymentsOnly: this.context.aiwynPaymentsOnly,
        dateRangeDays: this.context.dateRangeDays,
        searchTerm: this.context.searchTerm,
        columnFilterIds: this.context.columnFilterIds,
      }),
    };
  }
}
